import { z } from "zod";
import { racingEventStatusEnum } from "./racingEvent";

const outcomeResultEnum = z.enum(["WIN", "LOSE", "UNDECIDED", "VOID"]);
const outcomeTypeEnum = z.enum([
  "FRANCHISE",
  "FRANCHISE_HOME",
  "FRANCHISE_AWAY",
  "PLAYER",
  "PLAYER_HOME",
  "PLAYER_AWAY",
  "DRAW",
  "RUNNER_WIN",
  "RUNNER_TOP_2",
  "RUNNER_TOP_3",
  "RUNNER_TOP_4",
]);

const pickemsCompetitor = z.object({
  abbreviation: z.string().nullish(),
  id: z.string(),
  type: z.enum(["FRANCHISE", "PLAYER"]).nullish(),
  iconUri: z.string().optional(),
  raceId: z.string().optional(),
  number: z.number().nullish(),
  name: z.string(),
  status: z.string().nullish(),
  startingPosition: z.string().nullish(),
  hcpDraw: z.string().nullish(),
  finishPosition: z.number().nullish(),
  age: z.string().nullish(),
  sex: z.string().nullish(),
  weight: z.string().nullish(),
  jockey: z.string().nullish(),
  trainer: z.string().nullish(),
  scratched: z.boolean().nullish(),
  scratchedAt: z.string().nullish(),
  emergency: z.boolean().nullish(),
  formAttributes: z.record(z.string(), z.string()).nullish(),
  comment: z.string().nullish(),
  visible: z.boolean().nullish(),
  nationality: z.string().nullish(),
});

const pickemsMarketOutcome = z.object({
  competitor: pickemsCompetitor.nullish(), // if the outcome type is DRAW we won't have competitor data
  competitorId: z.string().nullish(), // if the outcome type is DRAW we won't have competitor data
  marketId: z.string(),
  bettingMarketId: z.string().nullish(),
  bettingOutcomeId: z.string().nullish(),
  openingPoints: z.number().optional(),
  recentPoints: z.union([z.array(z.number()), z.number()]).optional(),
  closingPoints: z.union([z.array(z.number()), z.number()]).optional(),
  id: z.string(),
  points: z.number(),
  odds: z.number().nullish(),
  result: outcomeResultEnum,
  type: outcomeTypeEnum,
});

const pickemsMarketEvent = z.object({
  attributes: z
    .object({
      categoryName: z.string().nullish(),
      matchMode: z.string().nullish(),
      tournamentName: z.string().nullish(),
      venueName: z.string().nullish(),
      raceNumber: z.string().nullish(),
      distance: z.string().nullish(),
      venueState: z.string().nullish(),
      venueCountry: z.string().nullish(),
      silksUrl: z.string().nullish(),
      raceStatus: racingEventStatusEnum.nullish(),
    })
    .catchall(z.unknown()),
  competitors: z.record(z.string(), z.unknown()),
  eventId: z.string(),
  eventName: z.string(),
  eventType: z.enum(["MATCH", "RACE", "SEASON", "TOURNAMENT"]),
  hub: z.enum(["esports", "sports", "racing"]),
  id: z.string(),
  scheduledStartTime: z.iso.datetime(),
  sport: z.string(),
  streamUri: z.string().optional(),
  tournamentId: z.string().nullish(),
  meetingId: z.string().nullish(),
  isLinkedToPickems: z.boolean().nullish(),
});

const pickemsMarket = z.object({
  bettingMarketId: z.string().optional(),
  event: pickemsMarketEvent,
  eventReferenceId: z.string(),
  id: z.string(),
  outcomes: z.record(z.string(), pickemsMarketOutcome).optional(),
  status: z.enum([
    "ACTIVE",
    "SUSPENDED",
    "HANDED_OVER",
    "SETTLED",
    "CANCELLED",
    "DEACTIVATED",
    "UNAVAILABLE",
    "INVALID",
  ]),
  scheduledStartTime: z.iso.datetime(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  expectedMarketType: z.string().nullish(),
});

const pickemsContestMarketCounts = z.object({
  active: z.number(),
  total: z.number(),
});

const pickemsContestExchangeRates = z.object({
  AUD: z.number().optional(),
  CAD: z.number().optional(),
  EUR: z.number().optional(),
  NZD: z.number().optional(),
});

const pickemsEntrySelectionEvent = z.object({
  eventId: z.string(),
  hub: z.string(),
  eventName: z.string(),
  tournamentId: z.string().optional(),
  sport: z.string(),
  scheduledStartTime: z.iso.datetime(),
  attributes: z
    .object({
      categoryName: z.string().nullish(),
      matchMode: z.string().nullish(),
      tournamentName: z.string().nullish(),
      silksUrl: z.string().nullish(),
      seasonName: z.string().nullish(),
    })
    .catchall(z.unknown()),
  eventType: z.string(),
  id: z.string(),
});

const pickemsEntryOutcomeBase = z.object({
  outcomeId: z.string(),
  outcomeName: z.string().optional(),
  result: outcomeResultEnum,
  competitor: pickemsCompetitor.nullish(),
  competitorId: z.string().nullish(),
  marketId: z.string(),
  outcomeType: outcomeTypeEnum.optional(),
  points: z.number(),
  marketStatus: z.string().optional(),
  marketName: z.string().nullish(),
});

const pickemsEntrySelectionsMapItem = pickemsEntryOutcomeBase.extend({
  event: pickemsEntrySelectionEvent,
  replacementOutcome: pickemsEntryOutcomeBase.nullish(),
});

const pickemsEntryData = z.object({
  entryId: z.number(),
  currency: z.string(),
  paidOut: z.number(),
  potentialPayout: z.number().nullish(),
  status: z.enum(["WON", "PLACED", "CANCELLED", "LOST", "LIVE"]),
  points: z.number(),
  place: z.number().optional(),
  contestNumber: z.number(),
  createdAt: z.iso.datetime(),
  selectionsMap: z.record(z.string(), pickemsEntrySelectionsMapItem).optional(),
  contestTitle: z.string(),
  promoCash: z.boolean().optional(),
});

const pickemsContestLeaderboardEntryUser = z.object({
  username: z.string(),
  id: z.string(),
  avatarUri: z.string().optional(),
});

const pickemsContestLeaderboardEntry = z.object({
  id: z.string(),
  paidOut: z.number(),
  place: z.number().optional(),
  points: z.number(),
  selectionCount: z.number(),
  status: z.enum([
    "PENDING",
    "LIVE",
    "CANCELLED",
    "COMPLETED",
    "WON",
    "PLACED",
    "LOST",
  ]),
  totalSelectedPoints: z.number(),
  winningSelectionCount: z.number(),
  user: pickemsContestLeaderboardEntryUser,
});

const pickemsContestEntries = z.object({
  live: z.number(),
  total: z.number(),
});

const prizeTiers = z.object({
  startRank: z.number(),
  endRank: z.number(),
  prize: z.number(),
});

const customPrizeTier = z.object({
  startRank: z.number(),
  endRank: z.number(),
  prize: z.string(),
});

const pickemsContest = z.object({
  cardImageUri: z.string().optional(),
  category: z.string(),
  contestNumber: z.number(),
  description: z.string().optional(),
  entries: pickemsContestEntries.optional(),
  exchangeRates: pickemsContestExchangeRates.optional(),
  marketCounts: pickemsContestMarketCounts,
  moreMarketsExpected: z.boolean(),
  prizePool: z.number().array(),
  prizeTiers: z.array(prizeTiers).optional(),
  rules: z.string().optional(),
  scheduledStartTime: z.iso.datetime().optional(),
  sportCategories: z.array(z.enum(["CONVENTIONAL", "ESPORTS", "RACING"])),
  sports: z.string().array(),
  status: z.enum(["PENDING", "LIVE", "CANCELLED", "COMPLETED", "ACTIVE"]),
  title: z.string(),
  promoCash: z.boolean().optional(),
  includedCampaignIds: z.string().array().nullish(),
  excludedCampaignIds: z.string().array().nullish(),
  currency: z.string().optional(),
  hasCustomPrizePool: z.boolean().optional(),
  customPrizePoolHeader: z.string().optional(),
  customPrizePoolDescription: z.string().optional(),
  customPrizeTiers: z.array(customPrizeTier).optional(),
});

type PickemsMarket = z.infer<typeof pickemsMarket>;
type PickemsContest = z.infer<typeof pickemsContest>;
type PickemsSelection = z.infer<typeof pickemsEntrySelectionsMapItem>;
type PickemsMarketOutcome = z.infer<typeof pickemsMarketOutcome>;
type PickemsCompetitor = z.infer<typeof pickemsCompetitor>;
type PickemsPrizeTiers = z.infer<typeof prizeTiers>;
type PickemsContestLeaderboardEntry = z.infer<
  typeof pickemsContestLeaderboardEntry
>;

export type {
  PickemsMarket,
  PickemsContest,
  PickemsSelection,
  PickemsMarketOutcome,
  PickemsCompetitor,
  PickemsPrizeTiers,
  PickemsContestLeaderboardEntry,
};

export {
  pickemsMarket,
  pickemsContest,
  pickemsContestLeaderboardEntry,
  pickemsEntryData,
};
